import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import moment from 'moment';
import {
	Card,
	Row,
	Col,
	Table,
	Typography,
	Spin,
	Divider,
	Tooltip,
	message,
	Button,
	Popconfirm,
	Descriptions,
} from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import NativeNumber from '../../component/Numeral';
import SearchFilter from '../../component/Filter';
import { useAppSelector, useAppDispatch } from '../../methods/redux/hooks';
import {
	getSwitchWalletBalances,
	triggerSwitchWalletRemittance,
} from '../../methods/redux/reducers/global-webhooks';
import getExplorerLink from './getExplorerLink';
import remakeQueryString from '../../methods/utils/remake-query-string';
import { commas, truncateAddress } from '../../methods/utils/general-utils';
import {
	getCurrencies,
	getBlockchainNetworks,
} from '../../methods/redux/reducers/currency';

const { Text, Title } = Typography;

interface SelectOption {
	name: string;
	value: string;
}

interface TriggerRemittanceProps {
	merchantId: string;
	network?: string;
	currency?: string;
	walletAddresses: string[];
}

const SwitchWalletBalances = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const merchantIdParams = searchParams.get('merchantId') || '';
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});

	const { loadingBalance, walletBalances, currentRemittanceCompany } =
		useAppSelector((store) => store.globalWebhooks);

	const { currencies, blockchains } = useAppSelector(
		(store) => store.currencies
	);

	const balances = walletBalances?.data?.balances || [];

	const currencySelect: SelectOption[] = currencies.map((item: any) => ({
		name: item.symbol,
		value: String(item.switchWalletId), // Ensure string
	}));

	const blockchainSelect: SelectOption[] = blockchains.map((item: any) => ({
		name: item.abbreviation,
		value: String(item.switchWalletEnum), // Ensure string
	}));

	useEffect(() => {
		window.scroll(0, 0);
	}, []);

	if (typeof window !== 'undefined') {
		window.addEventListener('error', (e: any) => {
			const msg = e?.message || '';
			if (
				msg.includes(
					'ResizeObserver loop completed with undelivered notifications'
				)
			) {
				e.stopImmediatePropagation();
			}
		});
	}

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());

		if (Object.keys(queryData).length > 0) {
			dispatch(getSwitchWalletBalances({ ...queryData }));
		} else {
			setSearchParams({
				merchantId: queryData.merchantId ?? '',
			});
		}

		dispatch(getCurrencies({}));
		dispatch(getBlockchainNetworks());
	}, [searchParams]);

	const triggerRemittance = async (record: any) => {
		const triggerState: TriggerRemittanceProps = {
			merchantId: merchantIdParams,
			walletAddresses: [record.walletAddress],
			network: record.network,
			currency: record.currency,
		};
		try {
			await dispatch(triggerSwitchWalletRemittance(triggerState));
		} catch (error: any) {
			// setOpenModal(false);
			message.error(error.response.data.message);
		}
	};

	const columns = [
		{
			title: 'S/N',
			dataIndex: 'number',
			key: 'number',
			render: (_: any, __: any, index: number) => (
				<strong>
					{(pagination.current - 1) * pagination.pageSize + index + 1}
				</strong>
			),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			render: (_: any, record: any) => (
				<div>
					<Popconfirm
						title="Are you sure?"
						onConfirm={() => triggerRemittance(record)}
					>
						<Button type="primary">Trigger Remittance</Button>
					</Popconfirm>
				</div>
			),
		},
		{
			title: 'Balance',
			dataIndex: 'balance',
			key: 'balance',
			render: (balance: any, record: any) => (
				<strong>
					<NativeNumber value={balance} decimalPlaces={4} />{' '}
					<Text strong>{record?.currency}</Text>
				</strong>
			),
		},
		{ title: 'Currency', dataIndex: 'currency', key: 'currency' },
		{ title: 'Network', dataIndex: 'network', key: 'network' },
		{ title: 'Id', dataIndex: 'id', key: 'id' },
		{
			title: 'Wallet Address',
			dataIndex: 'walletAddress',
			key: 'walletAddress',
			render: (walletAddress: string, record: any) => (
				<>
					<Tooltip title="Click to copy address" color="green">
						<span
							role="none"
							onClick={() => {
								navigator.clipboard.writeText(walletAddress);
								message.success('Address copied');
							}}
						>
							{truncateAddress(walletAddress)}
						</span>
					</Tooltip>
					<Tooltip title="View on Explorer" color="blue">
						<Link
							to={getExplorerLink(record.network, walletAddress)}
							target="_blank"
							rel="noreferrer"
						>
							<FullscreenOutlined />
						</Link>
					</Tooltip>
					<Link
						to={`/compliance/switch-wallet/wallet-balances?merchantId=${merchantIdParams}&walletAddress=${walletAddress}`}
						target="_blank"
						rel="noreferrer"
					>
						<Button type="default">View Balance</Button>
					</Link>

					<Link
						to={`/users/all-users/${walletAddress}`}
						target="_blank"
						rel="noreferrer"
					>
						<Button type="default">View User</Button>
					</Link>
				</>
			),
		},
		{ title: 'Address Index', dataIndex: 'addressIndex', key: 'addressIndex' },
		{
			title: 'Is Remittable',
			dataIndex: 'isRemittable',
			key: 'isRemittable',
			render: (isRemittable: any) => (
				<strong>{isRemittable ? 'True' : 'False'}</strong>
			),
		},
		{
			title: 'Min. Remittance Threshold',
			dataIndex: 'minimumRemittanceThreshold',
			key: 'minimumRemittanceThreshold',
		},
		{ title: 'Merchant Name', dataIndex: 'merchantName', key: 'merchantName' },

		{
			title: 'Last Updated',
			dataIndex: 'lastUpdated',
			key: 'lastUpdated',
			render: (lastUpdated: string) => (
				<p>
					{lastUpdated
						? moment(lastUpdated).format('MMM DD, YY / hh:mm:ss a')
						: '--'}
				</p>
			),
		},
	];

	return (
		<div style={{ paddingTop: 24 }}>
			<header style={{ marginBottom: 32 }}>
				<Title level={3}>SwitchWallet Wallet Balances</Title>
				{currentRemittanceCompany?.businessName && (
					<Title level={4}>
						Company: {currentRemittanceCompany?.businessName ?? '--'}
					</Title>
				)}
			</header>

			{/* Filters */}
			<Card>
				<SearchFilter
					title="Search Wallet Balances"
					searchFilters={[
						{
							label: 'Currency',
							name: 'currency',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'Blockchain Network',
							name: 'network',
							htmlElement: 'select',
							selectOptions: blockchainSelect,
						},
						{
							label: 'Wallet Address',
							name: 'walletAddress',
							htmlElement: 'input',
						},
					]}
					retrieveQueryString={updateQueryString}
				/>
			</Card>

			{/* Balances Summary Cards */}
			<Card style={{ marginBottom: 24 }}>
				{loadingBalance ? (
					<Spin
						size="large"
						style={{ display: 'block', margin: '40px auto' }}
					/>
				) : (
					<>
						<Descriptions
							title="Wallet Balance Summary"
							bordered
							column={2}
							style={{ marginBottom: '24px' }}
						>
							<Descriptions.Item label="Total Wallets">
								{walletBalances?.data?.summary?.totalWallets ? (
									<Text strong>
										<NativeNumber
											value={walletBalances?.data?.summary?.totalWallets}
											decimalPlaces={4}
										/>
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Total Remittable">
								{walletBalances?.data?.summary?.totalRemittable ? (
									<Text strong>
										{commas(walletBalances?.data?.summary?.totalRemittable)}
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Total Non Remittable">
								{walletBalances?.data?.summary?.totalNonRemittable ? (
									<Text strong>
										<NativeNumber
											value={walletBalances?.data?.summary?.totalNonRemittable}
											decimalPlaces={4}
										/>
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Remittable Percentage">
								{walletBalances?.data?.summary?.remittablePercentage ? (
									<Text strong>
										<NativeNumber
											value={
												walletBalances?.data?.summary?.remittablePercentage
											}
											decimalPlaces={4}
										/>
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>

							<Descriptions.Item label="Total Balance Value">
								{walletBalances?.data?.summary?.totalBalanceValue ? (
									<Text strong>
										<NativeNumber
											value={walletBalances?.data?.summary?.totalBalanceValue}
											decimalPlaces={4}
										/>
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Total Remittable Value">
								{walletBalances?.data?.summary?.totalRemittableValue ? (
									<Text strong>
										<NativeNumber
											value={
												walletBalances?.data?.summary?.totalRemittableValue
											}
											decimalPlaces={4}
										/>
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>
							<Descriptions.Item label="Total Non Remittable Value">
								{walletBalances?.data?.summary?.totalNonRemittableValue ? (
									<Text strong>
										<NativeNumber
											value={
												walletBalances?.data?.summary?.totalNonRemittableValue
											}
											decimalPlaces={4}
										/>
									</Text>
								) : (
									'--'
								)}
							</Descriptions.Item>
						</Descriptions>

						{walletBalances?.data?.summary?.balanceByCurrency?.length > 0 && (
							<Card title="Balance By Currency">
								<Row gutter={16} style={{ marginBottom: 16 }}>
									{walletBalances?.data?.summary?.balanceByCurrency?.map(
										(balance: any) => (
											<Col xs={24} sm={12} lg={6} key={balance?.currency}>
												<Card>
													<Text>
														<strong>{`${balance.currency} (${balance.network})`}</strong>
													</Text>
													<p style={{ marginTop: '8px' }}>
														Count:
														<NativeNumber
															value={balance.count}
															decimalPlaces={4}
														/>
													</p>
													<p>
														Remittable Count:
														<NativeNumber
															value={balance.remittableCount}
															decimalPlaces={4}
														/>
													</p>
													<p>
														Total Balance:{' '}
														<NativeNumber
															value={balance.totalBalance}
															decimalPlaces={4}
														/>
													</p>
													<p>
														Remittable Balance:{' '}
														<NativeNumber
															value={balance.remittableBalance}
															decimalPlaces={4}
														/>
													</p>
													<p>
														Non Remittable Balance:{' '}
														<NativeNumber
															value={balance.nonRemittableBalance}
															decimalPlaces={4}
														/>
													</p>
													<p>
														Min. Remittance Threshold:{' '}
														<NativeNumber
															value={balance.minimumRemittanceThreshold}
															decimalPlaces={4}
														/>
													</p>
												</Card>
											</Col>
										)
									)}
								</Row>
							</Card>
						)}

						<Divider />
					</>
				)}
			</Card>

			{/* Report Table */}

			<Card title="Balances">
				{loadingBalance ? (
					<Spin
						size="large"
						style={{ display: 'block', margin: '40px auto' }}
					/>
				) : (
					<Table
						rowKey="remittanceId"
						dataSource={balances}
						columns={columns}
						pagination={{
							...pagination,
							onChange: (page, pageSize) => {
								setPagination({ current: page, pageSize });
							},
						}}
						scroll={{ x: 'max-content' }}
					/>
				)}
			</Card>
		</div>
	);
};

export default SwitchWalletBalances;
